import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";

function formatSavedAt(value) {
  if (!value) return "Not saved";

  try {
    return new Date(value).toLocaleString("en-DK", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

function getCustomerName(customer = {}) {
  return (
    customer?.name ||
    customer?.full_name ||
    customer?.company_name ||
    customer?.email ||
    ""
  );
}

function getQuoteCustomerName(quote = {}) {
  return quote.customer_name || getCustomerName(quote.customer);
}

export default function QuoteDetailsHeader({
  quote,
  setCustomer,
  onCustomerChange,
  quoteValidation,
  onSave,
  onUndo,
  canUndo = false,
  isBusy = false,
  lastSavedAt,
}) {
  const [customers, setCustomers] = useState([]);
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);

  const hasIssues = quoteValidation?.isValid === false;
  const hasValidation = !!quoteValidation;
  const hasUnsavedChanges = !!quote?.has_unsaved_changes;
  const customerName = getQuoteCustomerName(quote);

  const title = quote?.project_name || "Estimate & Proposal";

  const savedLabel = hasUnsavedChanges
    ? "Unsaved"
    : formatSavedAt(lastSavedAt || quote?.last_saved_at);

  useEffect(() => {
    async function loadCustomers() {
      const { data, error } = await supabase
        .from("customers")
        .select("id,name,email,phone,type,city")
        .order("name", { ascending: true });

      if (!error) setCustomers(data || []);
    }

    loadCustomers();
  }, []);

  const selectedCustomerId = quote?.customer?.id || quote?.customer_id || "";

  const customerOptions = useMemo(
    () =>
      customers.map((customer) => ({
        ...customer,
        label: getCustomerName(customer),
      })),
    [customers]
  );

  function handleSelectCustomer(customerId) {
  const nextCustomer =
    customerOptions.find((customer) => customer.id === customerId) || null;

  if (onCustomerChange) {
    onCustomerChange(nextCustomer);
  } else {
    setCustomer?.(nextCustomer);
  }

  setShowCustomerSelect(false);
}

  return (
    <header className="sticky top-0 z-30 border-b border-[#edebe9] bg-white/95 text-[#201f1e] backdrop-blur">
      <div className="flex h-14 items-center justify-between gap-4 px-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-sm font-semibold">{title}</h1>

            <div className="mt-0.5 flex items-center gap-2 text-xs text-[#605e5c]">
              <button
                type="button"
                onClick={() => setShowCustomerSelect((value) => !value)}
                className="rounded-sm border border-[#d2d0ce] bg-white px-2 py-1 font-medium text-[#323130] hover:bg-[#f3f2f1]"
              >
            {customerName ? `Customer: ${customerName}` : "Choose customer"}
              </button>

              <span>·</span>
              <span>{quote?.id ? `#${quote.id}` : "New quote"}</span>
              <span>·</span>
              <span>{savedLabel}</span>
            </div>
          </div>

          <div className="h-6 border-l border-[#d2d0ce]" />

          {hasUnsavedChanges && <StatusPill tone="warning">Unsaved</StatusPill>}
          {customerName && <StatusPill tone="neutral">Customer linked</StatusPill>}

          {hasValidation && (
            <StatusPill tone={hasIssues ? "warning" : "success"}>
              {hasIssues ? "Needs review" : "Validated"}
            </StatusPill>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <ActionButton onClick={onUndo} disabled={!canUndo || isBusy}>
            Undo
          </ActionButton>

          <ActionButton
            onClick={onSave}
            disabled={isBusy || !hasUnsavedChanges}
            primary
          >
            Save
          </ActionButton>

          <ActionButton>Export</ActionButton>
          <ActionButton>More</ActionButton>
        </div>
      </div>

      {showCustomerSelect && (
        <div className="border-t border-[#edebe9] bg-[#faf9f8] px-4 py-3">
          <select
            value={selectedCustomerId}
            onChange={(event) => handleSelectCustomer(event.target.value)}
            className="h-9 min-w-[320px] rounded-sm border border-[#d2d0ce] bg-white px-3 text-sm text-[#201f1e] outline-none focus:border-[#0f6cbd]"
          >
            <option value="">No customer</option>

            {customerOptions.map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.label}
                {customer.email ? ` · ${customer.email}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {hasIssues && quoteValidation?.issues?.length > 0 && (
        <div className="flex items-start gap-2 border-t border-[#f3d6a0] bg-[#fff4ce] px-4 py-2 text-xs text-[#8a6d1d]">
          <span className="font-medium">Review</span>
          <span className="truncate">
            {quoteValidation.issues.slice(0, 2).join(" · ")}
            {quoteValidation.issues.length > 2
              ? ` +${quoteValidation.issues.length - 2}`
              : ""}
          </span>
        </div>
      )}
    </header>
  );
}

function ActionButton({ children, onClick, disabled = false, primary = false }) {
  return (
    <button
      type="button"
      onClick={() => onClick?.()}
      disabled={disabled}
      className={[
        "rounded-sm px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50",
        primary
          ? "bg-[#201f1e] text-white hover:bg-[#323130]"
          : "text-[#323130] hover:bg-[#f3f2f1]",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function StatusPill({ children, tone = "neutral" }) {
  const styles = {
    success: "bg-[#dff6dd] text-[#107c10]",
    warning: "bg-[#fff4ce] text-[#8a6d1d]",
    neutral: "bg-[#f3f2f1] text-[#605e5c]",
  };

  return (
    <span
      className={["rounded-sm px-2 py-1 text-xs font-medium", styles[tone]].join(
        " "
      )}
    >
      {children}
    </span>
  );
}